import React, { useEffect, useState } from "react";
import { Box } from "@chakra-ui/react";
import { useNavigate } from "@remix-run/react";
import Lottie from "react-lottie-player";
import { useTutor } from "../../../context/AiTutorContext";
import { useRoot } from "../../../context/RootContext";
import { usePractice } from "../../../context/PracticeContext";
import { getRandomVinMessage } from "../../../network/LearnApiService";
import emotionalMessages from "../../../utils/emotionalScriptConstants";
import emotional_lotties from "../../../utils/emotionalLottieConstants";
import {
  tutorString,
  urlString,
  isNotEmptyOrNull,
} from "../../../utils/common-utils";
import { StorageService } from "../../../utils/StorageUtils";
import { SuccessStyles } from "./SuccessStyles";
import pkg from "@inrscr/coschool-ui-components";
import useStateRef from "react-usestateref";

const { GlobalButton } = pkg;

const Modal = ({ onClose, taskType }) => {
  const navigate = useNavigate();
  const { taskAnalytics, journeyId, selectedSubTopic, selectedTopic } =
    useTutor();
  const { resetPractice } = usePractice();
  const { selectedIndex, isStandalone, isStandaloneWithSubjectMapping } =
    useRoot();

  const [backUpObject, setBackUpObject, backUpObjectRef] = useStateRef(null);
  const [redirectFlag, setRedirectFlag] = useState(false);
  const [taskCategory, setTaskCategory] = useState("");
  const [userDetails, setUserDetails] = useState({
    firstName: "",
  });
  const [assistantText, setAssistantText] = useState("");
  const [appreciationTitle, setAppreciationTitle] = useState("");
  const [vinAnimation, setVinAnimation] = useState(
    emotional_lotties.successBelow60
  );
  const [isTaskAnalyticsPresent, setIsTaskAnalyticsPresent] = useState(false);
  const [percentage, setPercentage, percentageRef] = useStateRef(0);
  const [isInitialized, setIsInitialized] = useState(false);

  const getEmotionalMessageCode = () => {
    if (taskType === "assessment") {
      if (percentageRef?.current < 60) return 2;
      if (percentageRef?.current >= 60 && percentageRef?.current < 80) return 3;
      if (percentageRef?.current >= 80 && percentageRef?.current < 100)
        return 4;
    } else if (
      taskType === "guidedPractise" ||
      taskType === "guidedPractice" ||
      taskType === "learnPrerequisite" ||
      selectedIndex === 3
    ) {
      if (percentageRef?.current < 60) return 6;
      if (percentageRef?.current >= 60 && percentageRef?.current < 80) return 8;
      if (percentageRef?.current >= 80 && percentageRef?.current < 100)
        return 7;
    } else if (taskType === "learnSubtopic" || taskType === "learn") {
      if (percentageRef?.current < 60) return 10;
      if (percentageRef?.current >= 60 && percentageRef?.current < 80)
        return 11;
      if (percentageRef?.current >= 80 && percentageRef?.current < 100)
        return 12;
    }
    return -1; // for 100 percent
  };

  const getDefaultAssistantText = (code) => {
    if (code === -1) return "Keep it up!";

    const defaultEmotionalMessages = emotionalMessages[0];
    const messagesForCode = defaultEmotionalMessages[code];
    if (!messagesForCode || messagesForCode.length === 0) return "Keep it up!";

    const randomIndex = Math.floor(Math.random() * messagesForCode.length);
    return messagesForCode[randomIndex];
  };

  const fetchAssistantText = async (code) => {
    if (code === -1) return "Keep it up!";

    try {
      const response = await getRandomVinMessage(code);
      if (response?.data?.message && response?.data?.status === true) {
        let message = response.data.message;

        if (
          message.includes("@@studentName") ||
          message.includes("@@studentname")
        ) {
          message = message.replace(
            /@@studentName|@@studentname/g,
            userDetails.firstName
          );
        }

        if (message.includes("@@linebreak")) {
          message = message.replace(/@@linebreak/g, "\n");
        }

        return message;
      }
    } catch (error) {
      console.error("Error fetching message:", error);
    }

    return getDefaultAssistantText(code);
  };

  useEffect(() => {
    if (isInitialized) return;

    const initialize = async () => {
      const redirectObject = StorageService.getItem("redirectObject");
      if (redirectObject) {
        setRedirectFlag(true);
      }

      const storedTaskType = StorageService.getItem("taskType");
      if (storedTaskType) {
        setTaskCategory(storedTaskType);
      }

      const storedBackUp = StorageService.getItem("backUpObject");
      if (isNotEmptyOrNull(storedBackUp)) {
        setBackUpObject(storedBackUp);
        const calculatedPercentage =
          selectedIndex === 3
            ? 90
            : taskAnalytics?.percentage
            ? parseInt(taskAnalytics?.percentage, 10)
            : storedBackUp?.successObject?.percentage || 0;
        setPercentage(calculatedPercentage);
      }

      let details = StorageService.getItem("user_additional_details");
      details = typeof details === "object" ? details : JSON.parse(details);
      if (details) {
        setUserDetails(details);
      }

      if (taskAnalytics) {
        setIsTaskAnalyticsPresent(true);
      }

      const emotionalCode = getEmotionalMessageCode();
      const text = await fetchAssistantText(emotionalCode);
      setAssistantText(text);

      const firstName = userDetails.firstName || "";
      if (percentageRef?.current < 60) {
        setAppreciationTitle(`Great Effort, ${firstName}`);
        setVinAnimation(emotional_lotties?.successBelow60);
      } else if (percentageRef?.current >= 60 && percentageRef?.current < 80) {
        setAppreciationTitle(`Well Done, ${firstName}`);
        setVinAnimation(emotional_lotties?.success6080);
      } else if (percentageRef?.current >= 80 && percentageRef?.current < 100) {
        setAppreciationTitle(`Fantastic Job, ${firstName}`);
        setVinAnimation(emotional_lotties?.sucess80100);
      } else if (percentageRef?.current === 100) {
        setAppreciationTitle(`Outstanding Work, ${firstName}`);
        setVinAnimation(emotional_lotties?.success100);
      } else {
        setAppreciationTitle(`Well done on finishing! Review and build on it.`);
        setVinAnimation(emotional_lotties?.successBelow60);
      }
      if (!isStandalone && !isStandaloneWithSubjectMapping && !redirectFlag) {
        updateBackUpObject(emotionalCode);
      }
      setIsInitialized(true);
    };

    initialize();
  }, []);

  const updateBackUpObject = (emotionalCode) => {
    const successObject = {
      isTaskCompleted: true,
      percentage: percentageRef?.current,
      emotionalMessageCode: emotionalCode,
    };

    const updatedBackUpObject = {
      ...backUpObjectRef?.current,
      successObject: successObject,
    };

    setBackUpObject(updatedBackUpObject);

    StorageService.setItem("backUpObject", updatedBackUpObject);
  };

  const subjectName =
    selectedTopic?.topic?.topicInfo?.subject_name ||
    backUpObjectRef?.current?.subjectName ||
    "";
  const topicName =
    selectedTopic?.topic?.topicInfo?.name ||
    backUpObjectRef?.current?.topicName ||
    "";
  const subTopicName =
    selectedSubTopic?.subtopicInfo?.name ||
    backUpObjectRef?.current?.subtopicName ||
    "";

  const subjectMasterUuid =
    selectedSubTopic?.subjectId ||
    selectedSubTopic?.subtopicInfo?.subject?.[0] ||
    selectedTopic?.subject?.subjectMasterUuid ||
    backUpObjectRef?.current?.subjectMasterUuid ||
    "";

  const topicMasterUuid =
    selectedSubTopic?.taskAttributes?.topicMasterUuid ||
    selectedSubTopic?.subtopicInfo?.topic?.[0] ||
    selectedTopic?.topic?.topicMasterUuid ||
    backUpObjectRef?.current?.topicMasterUuid ||
    "";

  const handleButtonClick = (isReview) => {
    StorageService.setItem("ReviewJourneyId", journeyId);

    if (isReview) {
      if (redirectFlag || isStandalone) {
        navigate(`${tutorString}/review`, {
          state: {
            journeyId: journeyId,
          },
        });
      } else {
        StorageService.setItem("subjectName", subjectName || "");
        StorageService.setItem("topicName", topicName || "");
        StorageService.setItem("subTopicName", subTopicName || "");
        StorageService.setItem("subjectMasterUUid", subjectMasterUuid);
        StorageService.setItem("topicMasterUUid", topicMasterUuid);

        navigate(`${urlString}/review`, {
          state: {
            // journeyId: journeyId,
            subjectMasterUuid: subjectMasterUuid,
            topicMasterUuid: topicMasterUuid,
          },
        });
      }
    } else {
      resetPractice();
      if (!isStandalone && !isStandaloneWithSubjectMapping) {
        navigate(`${urlString}/home`, { replace: true });
      } else {
        navigate(`${tutorString}/practice`, { replace: true });
      }
    }
  };

  return (
    <Box sx={SuccessStyles()?.mainContainer}>
      <Box sx={SuccessStyles()?.successContainer}>
        <Box sx={SuccessStyles()?.successSubContainer}>
          <Box sx={SuccessStyles()?.lottieContainer}>
            <Lottie
              loop
              animationData={vinAnimation}
              play
              style={{
                width: "40%",
                height: "70%",
              }}
            />
          </Box>

          <Box sx={SuccessStyles()?.appreciationTitleContainer}>
            {appreciationTitle}
          </Box>

          <Box sx={SuccessStyles()?.appreciationDescriptionContainer}>
            {String(assistantText)
              .split("\n")
              .map((line, index) => (
                <React.Fragment key={index}>
                  {line}
                  {index < assistantText.split("\n").length - 1 && <br />}
                </React.Fragment>
              ))}
          </Box>

          <Box sx={SuccessStyles()?.buttonContainer}>
            {!redirectFlag && (
              <GlobalButton
                key={"item-0"}
                onClick={() => handleButtonClick(false)}
                buttonText={"Back to homework"}
                buttonType="Secondary"
                size="medium"
                state="Enabled"
              />
            )}

            {taskCategory != 2 && (
              <GlobalButton
                key={"item-1"}
                onClick={() => handleButtonClick(true)}
                buttonText={"Let's review"}
                buttonType="Primary"
                size="medium"
                state="Enabled"
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Modal;
